// src/App.tsx

import React, { useEffect, useState } from 'react';
import { createBrowserRouter, Link } from 'react-router-dom';
import {
  Menu,
  X,
  Shield,
  Mail,
  Globe,
  Network,
  Box,
  Code2,
  MonitorPlay,
  Building,
  Terminal,
  Rocket,
  Zap,
} from 'lucide-react';
import TopologyMap from './TopologyMap';
import PricingSection from './PricingSection';
import ResponsiveDemo from './Demo';
import WaitlistForm from './WaitlistForm';
import logoIcon from './logo.svg';
import howItWorks from './howItWorks.svg';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [topoInstance, setTopoInstance] = useState<any>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (id: string) => {
    const [hash, queryString] = id.split('?');

    // Scroll to the section
    if (hash === "#" || hash === "") {
      // Scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    // Update the URL with the query string
    const newUrl = queryString ? `${hash}?${queryString}` : hash;
    window.history.pushState({}, '', newUrl);

    // Manually trigger the `ApplicationForm` to react to the URL change
    window.dispatchEvent(new Event('popstate'));
  };

  const topologyData = {
    nodes: [
      { id: 'router1', iconName: 'network.router', label: 'R1', layer: 10, customAttributes: {name: 'router1'}},
      { id: 'router2', iconName: 'network.router', label: 'R2', layer: 20, customAttributes: {name: 'router2'}}, 
      { id: 'router3', iconName: 'network.router', label: 'R3', layer: 20, customAttributes: {name: 'router2'}},
    ],
    edges: [
      {
        source: 'router1',
        target: 'router2',
        sourceInterface: { id: 'Gi0/0', labelText: 'Gi0/0' },
        targetInterface: { id: 'Gi0/0', labelText: 'Gi0/0' }
      },
      {
        source: 'router1',
        target: 'router3',
        sourceInterface: { id: 'Gi0/1', labelText: 'Gi0/1' },
        targetInterface: { id: 'Gi0/1', labelText: 'Gi0/1' }
      },
      {
        source: 'router2',
        target: 'router3',
        sourceInterface: { id: 'Gi0/1', labelText: 'Gi0/1' },
        targetInterface: { id: 'Gi0/0', labelText: 'Gi0/0' }
      },
    ],
    groups: [
      {
        id: 'routers',
        name: 'Routers',
        memberNodes: ['router1', 'router2', 'router3']
      }
    ]
  }

  const topologyConfig = {
    layouts: {
      defaultLayout: 'layered', 
      layered: {
          orientation: 'vertical',
          order: 'ascending',
          sortOrder: ['group.id', 'id'],
          minNodeDistance: 85,
          minLayerDistance: 50,
          maxNodeDistance: 10000,
          maxLayerDistance: 10000,
      },
    },
    theme: 'network-blue',
    groupHighlighting: true,
    adaptiveScaling: {
      enabled: true,
      scalingFactor: 0.8,
    },
    minZoom: 0.125,
    maxZoom: 3,
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.split('?')[0];
      if (hash) {
        scrollToSection(hash);
      }
    };
  
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Handle initial hash
  
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050614] via-[#0a1425] to-[#112340]">
      <nav className="fixed w-full bg-white/10 backdrop-blur-lg shadow-lg z-[9999] border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left section: Logo + Navigation Links */}
            <div className="flex items-center space-x-8">
              {/* Logo */}
              <a
                href="#"
                className="flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#');
                }}
              >
                <img
                  src={logoIcon}
                  alt="Logo"
                  className="w-10 h-10 transition-transform duration-300 ease-in-out transform hover:scale-110 hover:opacity-100"
                  draggable="false"
                />
                <span className="ml-2 text-xl font-bold text-white" draggable="false">
                  topoSphere
                </span>
              </a>

              {/* Desktop Navigation Links */}
              <div className="hidden md:flex items-center space-x-8">
                <a
                  href="#about"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#about');
                  }}
                  className="text-white/90 hover:text-white transition-all duration-200 text-base font-medium [text-shadow:_0_1px_4px_rgb(0_0_0_/_100%)] hover:scale-105"
                >
                  About
                </a>
              </div>
            </div>

            {/* Right section: Action Buttons + Mobile Menu */}
            <div className="flex items-center">
              {/* Desktop Action Buttons */}
              <div className="hidden md:flex items-center space-x-4">
                <a
                  href="#demo"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#demo');
                  }}
                  className="text-white/90 hover:text-white transition-all duration-200 text-base font-medium inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-1.5 rounded-md border border-white/10 hover:border-white/20 hover:scale-105"
                >
                  <MonitorPlay className="w-5 h-5" />
                  Start Guided Tour
                </a>
                <a
                  href="#waitlist"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#waitlist');
                  }}
                  className="bg-[#d35400]/90 hover:bg-[#d35400] text-white px-4 py-1.5 rounded-md text-base font-medium transition-all duration-200 inline-flex items-center gap-2 hover:scale-105 hover:shadow-lg hover:shadow-[#d35400]/20"
                >
                  <Rocket className="w-5 h-5" />
                  Join the Waitlist
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button onClick={toggleMenu} className="md:hidden ml-4">
                {isMenuOpen ? (
                  <X className="h-6 w-6 text-white" />
                ) : (
                  <Menu className="h-6 w-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-x-0 top-16 bg-[#050614]/95 backdrop-blur-md z-[9999] border-b border-white/10">
            <div className="px-4 py-3 space-y-3">
              <a
                href="#about"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#about');
                  setIsMenuOpen(false);
                }}
                className="block px-4 py-2 text-lg text-white/90 hover:text-white hover:bg-white/10 transition-colors rounded-lg text-center"
              >
                About
              </a>
              <a
                href="#demo"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#demo');
                  setIsMenuOpen(false);
                }}
                className="block px-4 py-2 text-lg text-white/90 hover:text-white hover:bg-white/10 transition-colors rounded-lg text-center"
              >
                Demo
              </a>
              <a
                href="#waitlist"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#waitlist');
                  setIsMenuOpen(false);
                }}
                className="block px-4 py-2 text-lg text-white hover:text-white hover:bg-white/10 transition-colors rounded-lg text-center bg-[#d35400]/90"
              >
                Join the Waitlist
              </a>
            </div>
          </div>
        )}
      </nav>

      <section className="min-h-[100vh] flex flex-col justify-center pt-20 md:pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/src/earth-connections.webp')] opacity-[0.05] bg-cover bg-center bg-[position:50%_110%] z-0"></div>
        <div className="max-w-7xl mx-auto text-center relative">

          <div className="inline-block mb-6 sm:mb-12">
            <div className="px-3 sm:px-8 py-1.5 sm:py-3 rounded-full bg-blue-500/15 backdrop-blur-md border border-blue-500/20 shadow-lg shadow-blue-500/5">
              <p className="text-[10px] leading-tight sm:text-base text-blue-400 font-medium">
                Built on the success of NextBox-UI, a top NetBox plugin trusted by hundreds of companies. 570+ GitHub stars.
              </p>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 sm:mb-8 leading-tight bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent whitespace-normal max-w-[1400px] mx-auto">
            Network Visualization That Just Works
          </h1>

          <p className="text-sm sm:text-base lg:text-xl text-white/90 mb-10 sm:mb-12 max-w-4xl mx-auto leading-relaxed">
            Developers save time with network-specific features. Users enjoy smooth, interactive network maps.
          </p>
          <p className="text-sm sm:text-base lg:text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            COMING SOON
          </p>

          <div className="flex flex-col items-center justify-center gap-6 w-full max-w-sm sm:max-w-2xl mx-auto mt-16">
          
{/* Primary CTA buttons */}
<div className="w-full flex flex-col sm:flex-row sm:max-w-xl mx-auto gap-2 sm:gap-4">
  <a
    href="#demo"
    onClick={(e) => {
      e.preventDefault();
      scrollToSection('#demo');
    }}
    className="
      block w-full
      bg-gradient-to-r from-white/10 via-white/[0.15] to-white/20
      text-white px-6 sm:px-8 py-4 rounded-lg text-lg font-semibold
      hover:opacity-90 shadow-lg border border-white/10 no-underline
      transition-colors duration-300
      z-10
    "
  >
    <div className="flex items-center justify-center gap-2">
      <MonitorPlay className="w-5 h-5 flex-shrink-0" />
      <span>Start Guided Tour</span>
    </div>
  </a>

  <a
    href="#waitlist"
    onClick={(e) => {
      e.preventDefault();
      scrollToSection('#waitlist');
    }}
    className="
      block w-full
      bg-gradient-to-r from-[#d35400] to-[#e67e22]
      text-white px-6 sm:px-8 py-4 rounded-lg text-lg font-semibold
      hover:opacity-90 shadow-lg no-underline
      transition-colors duration-300
      z-10
    "
  >
    <div className="flex items-center justify-center gap-2">
      <Rocket className="w-5 h-5 flex-shrink-0" />
      <span>Join the Waitlist</span>
    </div>
  </a>
</div>


            {/* How It Works Diagram */}
            <div className="w-full max-w-4xl mx-auto px-4 -mt-6 sm:-mt-2 lg:-mt-14">
              <img
                src={howItWorks}
                alt="How topoSphere works: Upload network data, get interactive visualization, and update in real-time"
                className="w-full h-auto opacity-90 hover:opacity-100 transition-opacity duration-300"
                draggable="false"
              />
            </div>

          </div>
        </div>
      </section>

      <section id="about" className="py-20 bg-gradient-to-b from-[#050614]/0 via-white/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            Why Choose topoSphere?
          </h2>
          <div className="max-w-7xl mx-auto mb-10">
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/10 hover:bg-white/[0.12] transition-all">
              <p className="text-lg text-white/90 leading-relaxed text-center">
                Network visualizations should be effortless for users and powerful for developers. But most tools don’t get it right—generic graph libraries demand extensive coding, and NMS maps are rigid, hard to embed, and even harder to adapt. topoSphere was built for networks from day one, saving you hundreds of hours while delivering professional, interactive maps that just work.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <Code2 className="h-12 w-12 text-blue-400 group-hover:scale-110 transition-transform duration-300 flex-shrink-0" />
                <h3 className="text-xl font-semibold text-white">Network-Map-as-Code</h3>
              </div>
              <p className="text-white/90">
                A code-first approach with a clean, high-level API lets you focus on scenarios and insights, not rendering. Define, update, and automate topologies with ease.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <Network className="h-12 w-12 text-[#60a5fa] flex-shrink-0" />
                <h3 className="text-xl font-semibold text-white">Smart Network Layouts</h3>
              </div>
              <p className="text-white/90">
                Purpose-built algorithms take care of layout—automated, intuitive, and just how you'd draw it. Because topology matters.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <Shield className="h-12 w-12 text-[#60a5fa] flex-shrink-0" />
                <h3 className="text-xl font-semibold text-white">Privacy by Design</h3>
              </div>
              <p className="text-white/90">
                100% client-side processing with zero dependencies ensures your sensitive network data never leaves your control.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <MonitorPlay className="h-12 w-12 text-[#60a5fa] flex-shrink-0" />
                <h3 className="text-xl font-semibold text-white">Truly Interactive Maps</h3>
              </div>
              <p className="text-white/90">
                Responsive maps for any screen or resolution. Pan, zoom, and animate with fluid gestures—making complex topologies instantly navigable, from big monitors to tablets.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <Box className="h-12 w-12 text-[#60a5fa] flex-shrink-0" />
                <h3 className="text-xl font-semibold text-white">Seamless Integration</h3>
              </div>
              <p className="text-white/90">
                A single lightweight Javascript bundle with zero dependencies. Deploy seamlessly in any framework or vanilla JS project.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-4 mb-4">
                <Building className="h-12 w-12 text-[#60a5fa] flex-shrink-0" />
                <h3 className="text-xl font-semibold text-white">Enterprise-Grade Flexibility</h3>
              </div>
              <p className="text-white/90">
                Scale from proof-of-concept to enterprise deployment. White-label ready with complete branding control for OEM success.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="demo" className="py-20 bg-gradient-to-b from-transparent via-white/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with transition */}
          <div 
            className={`transition-all duration-300 ease-in-out transform ${
              isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full h-0'
            }`}
          >
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              See topoSphere in Action
            </h2>
          </div>
          
          <div className="bg-[#050614]/80 border border-white/20 rounded-xl overflow-hidden">
            <div className="relative flex flex-col">
              {/* Desktop sidebar */}
              <div className="hidden md:block absolute left-0 top-0 bottom-0 w-64 bg-[#0a1425] border-r border-white/10">
                {topoInstance && (
                  <ResponsiveDemo 
                    topoInstance={topoInstance} 
                    variant="desktop"
                    onDemoStart={() => setIsHeaderVisible(false)}
                  />
                )}
              </div>
              
              {/* Main topology container */}
              <div className="w-full md:pl-64">
                <div className="w-full pb-[90%] md:pb-0 md:h-[600px] relative">
                  <div className="absolute inset-0">
                    <TopologyMap
                      topologyData={topologyData}
                      config={topologyConfig}
                      onInit={(instance) => setTopoInstance(instance)}
                    />
                  </div>
                </div>
              </div>

              {/* Mobile demo controls */}
              <div className="md:hidden">
                {topoInstance && (
                  <ResponsiveDemo 
                    topoInstance={topoInstance} 
                    variant="mobile"
                    onDemoStart={() => setIsHeaderVisible(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaitlistForm />

      <footer id="contact" className="bg-[#050614] text-white py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Company Info Column */}
            <div className="space-y-4">
              <div className="flex items-center">
                <img
                  src={logoIcon}
                  alt="Logo"
                  className="w-10 h-10 transition-transform duration-300 ease-in-out transform hover:scale-110"
                  draggable="false"
                />
                <span className="ml-2 text-xl font-bold text-white" draggable="false">
                  topoSphere
                </span>
              </div>
              <p className="text-white/90 max-w-xs">
                The Network Visualization SDK
              </p>
            </div>

            {/* Quick Links Column */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="#about" 
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('#about');
                    }} 
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a 
                    href="#demo" 
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('#demo');
                    }} 
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    Demo
                  </a>
                </li>
                <li>
                  <Link 
                    to="/privacy" 
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Contact</h3>
              <ul className="space-y-3">
                <li className="text-white/70">
                  <strong className="text-white">Support:</strong><br />
                  support@topoSphere.net
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright Bar */}
          <div className="border-t border-white/10 mt-12 pt-8 text-center">
            <p className="text-white/60">
              &copy; 2024-{new Date().getFullYear()} topoSphere. All rights reserved. Made in Dubai, UAE with ❤️
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
